import { ref, onUnmounted, readonly, getCurrentInstance } from 'vue'

interface CountdownOptions {
  initialSeconds?: number
  storageKey?: string
  autoStart?: boolean
}

export function useCountdownTimer(options: CountdownOptions = {}) {
  const {
    initialSeconds = 60,
    storageKey = 'countdownTimer',
    autoStart = true
  } = options

  const countdownSeconds = ref(0) // Internal seconds counter
  const countdown = ref('00:00') // Formatted countdown display
  const isActive = ref(false)
  const timer = ref<NodeJS.Timeout | null>(null)
  const finishedStorageKey = `${storageKey}_finished`

  // Format seconds to MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    const paddedMinutes = String(minutes).padStart(2, '0')
    const paddedSeconds = String(secs).padStart(2, '0')
    return `${paddedMinutes}:${paddedSeconds}`
  }

  // Update countdown display
  const updateCountdownDisplay = (): void => {
    countdown.value = formatTime(countdownSeconds.value)
  }

  // Check if timer was marked as finished
  const isTimerFinished = (): boolean => {
    try {
      return localStorage.getItem(finishedStorageKey) === 'true'
    } catch (error) {
      console.error('Error reading finished state from localStorage:', error)
      return false
    }
  }

  // Mark timer as finished
  const setTimerFinished = (): void => {
    try {
      localStorage.setItem(finishedStorageKey, 'true')
    } catch (error) {
      console.error('Error setting finished state in localStorage:', error)
    }
  }

  // Clear finished mark
  const clearTimerFinished = (): void => {
    try {
      localStorage.removeItem(finishedStorageKey)
    } catch (error) {
      console.error('Error clearing finished state from localStorage:', error)
    }
  }

  // Get saved countdown from localStorage
  const getSavedCountdown = (): { timeLeft: number; wasSaved: boolean } => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const data = JSON.parse(saved)
        const now = Date.now()
        const timeLeft = Math.max(0, Math.ceil((data.endTime - now) / 1000))
        return { timeLeft, wasSaved: true }
      }
    } catch (error) {
      console.error('Error reading countdown from localStorage:', error)
    }
    return { timeLeft: initialSeconds, wasSaved: false }
  }

  // Save countdown to localStorage
  const saveCountdown = (seconds: number): void => {
    try {
      const endTime = Date.now() + (seconds * 1000)
      localStorage.setItem(storageKey, JSON.stringify({ endTime }))
    } catch (error) {
      console.error('Error saving countdown to localStorage:', error)
    }
  }

  // Clear saved countdown from localStorage
  const clearSavedCountdown = (): void => {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error('Error clearing countdown from localStorage:', error)
    }
  }

  // Start the countdown timer
  const start = (seconds?: number): void => {
    stop() // Clear any existing timer

    const startSeconds = seconds ?? getSavedCountdown().timeLeft
    countdownSeconds.value = startSeconds
    updateCountdownDisplay()
    isActive.value = true

    if (startSeconds <= 0) {
      isActive.value = false
      clearSavedCountdown()
      setTimerFinished() // Mark as finished if starting at 0 or less
      return
    }

    clearTimerFinished() // Clear finished mark if starting a new countdown
    // Save the countdown to localStorage
    saveCountdown(startSeconds)

    timer.value = setInterval(() => {
      if (countdownSeconds.value > 0) {
        countdownSeconds.value--
        updateCountdownDisplay()
        // Update localStorage with remaining time
        if (countdownSeconds.value > 0) {
          saveCountdown(countdownSeconds.value)
        } else {
          clearSavedCountdown()
          isActive.value = false
          setTimerFinished() // Mark as finished when countdown reaches 0
        }
      } else {
        stop()
      }
    }, 1000)
  }

  // Stop the countdown timer
  const stop = (): void => {
    if (timer.value) {
      clearInterval(timer.value)
      timer.value = null
    }
    isActive.value = false
  }

  // Reset the countdown timer
  const reset = (seconds?: number): void => {
    stop()
    clearSavedCountdown()
    clearTimerFinished() // Clear finished mark on reset
    countdownSeconds.value = seconds ?? initialSeconds
    updateCountdownDisplay()
  }

  // Complete the countdown (clear everything)
  const complete = (): void => {
    stop()
    clearSavedCountdown()
    setTimerFinished() // Mark as finished on complete
    countdownSeconds.value = 0
    updateCountdownDisplay()
  }

  // Initialize countdown from saved state
  const initialize = (): void => {
    if (isTimerFinished()) {
      countdownSeconds.value = 0
      updateCountdownDisplay()
      isActive.value = false
      clearSavedCountdown() // Ensure no old timer data persists
      return
    }

    const { timeLeft, wasSaved } = getSavedCountdown()
    countdownSeconds.value = timeLeft
    updateCountdownDisplay()

    if (timeLeft <= 0 && wasSaved) {
        // If there was saved time but it has expired, mark as finished
        setTimerFinished()
        isActive.value = false
        clearSavedCountdown()
        countdownSeconds.value = 0
        updateCountdownDisplay()
        return
    }

    // Auto-start logic:
    // 1. If there's saved time from localStorage and it's > 0, start with that
    // 2. If autoStart is true and no saved data, start with initial seconds
    if (autoStart) {
      if (wasSaved && timeLeft > 0) {
        start(timeLeft)
      } else if (!wasSaved && timeLeft > 0) { // This condition seems to imply initialSeconds > 0 if timeLeft is initialSeconds
        start(timeLeft) // Starts with initialSeconds if no saved data and initialSeconds > 0
      }
    }
  }

  // Cleanup on component unmount (only if within a component context)
  const instance = getCurrentInstance()
  if (instance) {
    onUnmounted(() => {
      stop()
    })
  }

  // Initialize on creation
  initialize()

  return {
    countdown: readonly(countdown),
    isActive: readonly(isActive),
    start,
    stop,
    reset,
    complete,
    initialize
  }
}
